import { useCallback, useState } from 'react'
import type React from 'react'
import { taskMenuTargetForKey } from './taskMenuShortcuts'
import type { TaskMenuTarget } from './TaskMenu.types'

/**
 * Was `TaskMenu` und `SubtaskMenu` gleich steuern (#205, #213).
 *
 * **Welches Unter-Menü offen steht — gesteuert, nicht Radix überlassen.** Radix hat für Menüs
 * einen Typeahead: Ein getippter Buchstabe SPRINGT auf die Zeile, die so beginnt, und klappt sie
 * nicht auf. Ein `W` neben „Wann …" verspräche damit etwas, das es nicht tut (gemeldet
 * 2026-09-11). Die Kürzel fängt deshalb der Kasten selbst ab (`handleContentKeyDown`), bevor der
 * Typeahead sie sieht, und öffnet das Ziel. Das Zeigerverhalten bleibt unberührt: Radix meldet
 * Hover und Klick weiter über `onOpenChange`.
 *
 * **Schließen** (`close`) — nachdem ein Inhalt einen Wert geliefert hat. Radix baut
 * Kontextmenüs bewusst UNGESTEUERT: `ContextMenu.Root` kennt kein `open`, es schließt über die
 * Auswahl eines Items, einen Klick daneben oder Escape. Unsere Inhalte sind keine Radix-Items
 * (Kalender, Stufenwähler, Tag-Liste) — bleibt Escape, und den hört die DismissableLayer am
 * `document`.
 *
 * @param recurrenceLocked unter einer wiederkehrenden Aufgabe (#205): ⇧W öffnet nichts
 * @param onMove das Menü führt „Verschieben …“ (#257): ⇧⌘M schließt es und öffnet das Panel an der Zeile (#263)
 */
export function useTaskMenuSubs(recurrenceLocked: boolean, onMove?: () => void) {
  // „Erinnerung“ hat kein Kürzel (R ist vergeben) und ist deshalb kein `TaskMenuTarget` — offen stehen kann sie trotzdem.
  const [openSub, setOpenSub] = useState<TaskMenuTarget | 'reminders' | null>(null)

  const close = useCallback(() => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
  }, [])

  const handleContentKeyDown = useCallback((event: React.KeyboardEvent) => {
    const target = taskMenuTargetForKey(event)
    // ⇧⌘M (#257) öffnet „Verschieben …“ nur, wo das Menü die Zeile hat.
    if (!target || (target === 'recurrence' && recurrenceLocked) || (target === 'move' && !onMove)) return
    event.preventDefault()
    // Radix' Typeahead hört auf demselben Ereignis eine Ebene höher.
    event.stopPropagation()
    if (target === 'move') {
      // Erst zu, dann auf: Das Escape zum Schließen soll das neue Panel nicht gleich mitnehmen.
      close()
      onMove?.()
      return
    }
    setOpenSub(target)
  }, [recurrenceLocked, onMove, close])

  return { openSub, setOpenSub, close, handleContentKeyDown }
}
